import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { ArrowRight, MapPin, Star } from "lucide-react";
import spaceManagementService from "../../../services/host-apiServices/spaceManagementService";
import { generateLocationString, generateSpaceName, getEventName } from "../../../utils/spaceNamingUtils";
import logger from "../../../utils/logger";

type Pillar = "work" | "stay" | "play";

// Category IDs: work -> 1, stay -> 2, play -> 3 (hardcoded until the categories API is wired in)
const CATEGORY_IDS: Record<Pillar, number> = { work: 1, stay: 2, play: 3 };

// The design only shows the Work tab; Stay and Play copy follows the same pattern
const TAB_COPY: Record<Pillar, { intro: string; noun: string; cta: string }> = {
  work: {
    intro: "Discover flexible workspaces designed for productivity, collaboration and focus.",
    noun: "Workspaces",
    cta: "Explore Workspaces",
  },
  stay: {
    intro: "Find quality accommodation for business trips, short stays and longer visits.",
    noun: "Stays",
    cta: "Explore Stays",
  },
  play: {
    intro: "Book curated experiences for wellness, dining, adventure and events.",
    noun: "Experiences",
    cta: "Explore Experiences",
  },
};

const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1527192491265-7e15c55b1ed2?auto=format&fit=crop&w=600&q=80";

export default function ExploreSection() {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<Pillar>("work");
  const [spaces, setSpaces] = useState<any[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  // The section is hidden until at least one pillar has something to show
  const [hasListings, setHasListings] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchPublicSpaces = async () => {
      setLoading(true);
      try {
        const res = await spaceManagementService.searchPublicSpacesUnauthenticated({
          categoryId: CATEGORY_IDS[activeTab],
          pageSize: 4,
          status: "PUBLISHED",
        });
        const responseData: any = res?.data;
        const items = Array.isArray(responseData?.content)
          ? responseData.content
          : Array.isArray(responseData?.data)
            ? responseData.data
            : Array.isArray(responseData)
              ? responseData
              : [];
        const count = responseData?.totalElements ?? responseData?.total ?? responseData?.totalItems;
        if (isMounted) {
          setSpaces(items);
          setTotal(typeof count === "number" ? count : null);
          if (items.length > 0) setHasListings(true);
        }
      } catch (err) {
        logger.warn("Failed to load public featured spaces:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchPublicSpaces();
    return () => {
      isMounted = false;
    };
  }, [activeTab]);

  const copy = TAB_COPY[activeTab];

  // Nothing published yet: leave the section out entirely
  if (!loading && !hasListings && spaces.length === 0) return null;

  return (
    <section className="bg-[#f8fbff] py-20">
      <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <div className="flex flex-col items-center text-center">
          <span className="rounded-full bg-[#017bef] px-[16px] py-1.5 text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-white drop-shadow-[0_2px_6px_rgba(1,123,239,0.25)]">
            The Platform
          </span>
          <h2 className="pt-[20px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
            Explore Work, Stay &amp; Play
          </h2>
          <p className="max-w-[576px] pt-[12px] text-[16px] leading-6 text-[#717182]">
            Browse trusted workspaces, accommodation and curated experiences available on SPACIA today.
          </p>

          <div className="mt-12 flex gap-1 rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white p-1 shadow-[0_1px_1.5px_rgba(0,0,0,0.1),0_1px_1px_rgba(0,0,0,0.1)]">
            {(Object.keys(CATEGORY_IDS) as Pillar[]).map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`rounded-[14px] px-6 py-2.5 text-[14px] font-semibold capitalize leading-5 transition-colors ${
                  activeTab === tab
                    ? "bg-[#017bef] text-white shadow-[0_2px_4px_rgba(1,123,239,0.3)]"
                    : "text-[#717182] hover:text-[#0a0a0a]"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-start justify-between gap-[16px] pt-10 sm:flex-row sm:items-center">
          <div>
            <p className="text-[16px] font-semibold leading-6 text-[#0a0a0a]">{copy.intro}</p>
            {total !== null && (
              <p className="pt-0.5 text-[14px] font-semibold leading-5 text-[#017bef]">
                {total} Active {copy.noun}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => history.push(`/workspaces-search?type=${activeTab}`)}
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#017bef] px-[20px] py-2.5 text-[14px] font-semibold leading-5 text-white drop-shadow-[0_2px_6px_rgba(1,123,239,0.25)] transition-colors hover:bg-[#0062c0]"
          >
            {copy.cta}
            <ArrowRight className="size-3.5" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-[20px] pt-7 sm:grid-cols-2 lg:grid-cols-4">
          {loading ? (
            [1, 2, 3, 4].map(n => (
              <div
                key={n}
                className="animate-pulse overflow-hidden rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white"
              >
                <div className="h-44 bg-[#f5f8ff]" />
                <div className="space-y-2 p-[16px]">
                  <div className="h-4 w-3/4 rounded bg-gray-200" />
                  <div className="h-3 w-1/2 rounded bg-gray-200" />
                </div>
              </div>
            ))
          ) : spaces.length > 0 ? (
            spaces.map((item: any, idx: number) => {
              const firstInstance = item.instances?.[0];
              const displayName =
                getEventName(item, firstInstance) ||
                (item.hostDisplayName
                  ? `${item.subCategory?.name || item.subCategoryName || item.description || "Space"} - ${item.hostDisplayName}`
                  : generateSpaceName(item, firstInstance, true));
              const image = item.media?.images?.[0] || item.imageUrl || item.img || FALLBACK_IMAGE;
              const badge = item.subCategory?.name || item.subCategoryName || item.category?.name || "Space";
              const rating = item.rating ?? item.averageRating;
              return (
                <div
                  key={item.id || idx}
                  className="overflow-hidden rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]"
                >
                  <div className="relative h-44 bg-[#f5f8ff]">
                    <img src={image} alt={displayName} className="absolute inset-0 size-full object-cover" />
                    <span className="absolute left-[12px] top-[12px] rounded-full bg-[#017bef] px-2 py-0.5 text-[10px] font-semibold leading-[15px] text-white">
                      {badge}
                    </span>
                  </div>
                  <div className="p-[16px]">
                    <h3 className="line-clamp-1 text-[14px] font-semibold leading-[19.25px] text-[#0a0a0a]">
                      {displayName}
                    </h3>
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <span className="flex min-w-0 items-center gap-1 text-[12px] leading-4 text-[#717182]">
                        <MapPin className="size-[11px] shrink-0" />
                        <span className="truncate">{generateLocationString(item, firstInstance)}</span>
                      </span>
                      {rating ? (
                        <span className="flex shrink-0 items-center gap-0.5 text-[12px] font-semibold leading-4 text-[#0a0a0a]">
                          <Star className="size-[11px] fill-[#017bef] text-[#017bef]" />
                          {Number(rating).toFixed(1)}
                        </span>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => history.push(`/space/${item.id}`)}
                      className="mt-[12px] w-full rounded-[14px] border-[1px] border-solid border-[#e5e7eb] py-2 text-center text-[12px] font-semibold leading-4 text-[#0a0a0a] transition-colors hover:bg-[#f5f8ff]"
                    >
                      View Listing
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            // Only reachable once another tab has listings, so the tabs stay available
            <div className="col-span-full py-8 text-center text-[14px] text-[#717182]">
              No active listings found for {activeTab}.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
